import { labeebEventsOf, type LabeebAuditPage } from "../../api/labeeb";
import { actorEmail, auditStamp, eventsOf, type AuditPage } from "../../api/mcp";
import type { AppConfig } from "../../config";
import { useResource, type Resource } from "../../hooks/useResource";
import { Notice, Panel } from "../ui";
import { AuditChangeDetail } from "./AuditChangeDetail";

/** Why an audit read failed, in a sentence. Named per kind: none reads as "nothing recorded". */
export function auditReadFailure(state: Exclude<Resource<unknown>, { kind: "loading" | "ready" }>): string {
  switch (state.kind) {
    case "signedout":
      return "Signed out — reload to see who changed this.";
    case "notfound":
      return "Audit route not mounted on this gateway.";
    case "denied":
    case "unavailable":
    case "invalid":
    case "error":
      return state.error.message;
  }
}

/** A target as an audit-route path, encoded per segment (see below). */
export function auditTargetPath(target: string): string {
  return target.split("/").map(encodeURIComponent).join("/");
}

/**
 * "Who changed this" — the audit trail for one specific thing.
 *
 * This is the audit route worth the most: it puts the answer where the
 * question is actually asked. Nobody wonders "what happened across the fleet"
 * while looking at a merchant; they wonder who moved THIS merchant's tier.
 *
 * Two cases nothing else in the API can answer:
 *   - a blocklist row is DELETED on unblock, so this is the only record that
 *     someone was ever blocked here, and who lifted it
 *   - a cap's `updated_by` is overwritten on every change, so this is the only
 *     record of who set the value before the current one
 *
 * `target` may contain a slash (`free/get_overview_report`). It is encoded per
 * SEGMENT, not whole: the gateway route is `{target+}`, greedy, and encoding
 * the slash as %2F would defeat the greedy match it exists for.
 */
export function RecentChanges({
  config,
  domain,
  target,
  limit = 5,
  product = "mcp",
}: {
  config: AppConfig;
  domain: string;
  target: string;
  limit?: number;
  /** Which product's audit this target lives in. Existing callers are MCP. */
  product?: "mcp" | "labeeb";
}) {
  const encoded = target.split("/").map(encodeURIComponent).join("/");
  const path = `/${product}/admin/audit/${domain}/${encoded}`;
  const { state } = useResource<AuditPage | LabeebAuditPage>(config, path, [product, domain, target]);

  // Quiet while loading — this is a footnote, not the page. Failures are
  // named: collapsing them into "nothing recorded" would report silence
  // where the server refused or could not look.
  if (state.kind === "loading") return null;

  if (state.kind !== "ready") {
    return (
      <Notice tone="amber" title="Could not read recent changes">
        {auditReadFailure(state)}
      </Notice>
    );
  }

  const events = (
    product === "labeeb"
      ? labeebEventsOf(state.data as LabeebAuditPage)
      : eventsOf(state.data as AuditPage)
  ).slice(0, limit);

  return (
    <Panel className="p-4">
      <h2 className="text-sm font-semibold">Recent changes</h2>

      {events.length === 0 ? (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Nothing recorded. Changes made before the audit log existed do not appear here.
        </p>
      ) : (
        <ul className="mt-2 space-y-3">
          {events.map((e, i) => (
              <li
                key={`${e.timestamp ?? "none"}-${i}`}
                className="border-b border-slate-950/5 pb-2 last:border-0 dark:border-white/10"
              >
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {auditStamp(e.timestamp, { dateOnly: true })} · {actorEmail(e.actor)} ·{" "}
                  <span className="ref">{e.action}</span>
                </p>
                <div className="mt-1">
                  <AuditChangeDetail
                    compact
                    before={e.before}
                    after={e.after}
                    action={e.action}
                    reason={e.reason}
                  />
                </div>
              </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
